/**
 * Candidate queries, and the reference lists the filters need.
 *
 * Every filter key below is the API's own query-parameter name, spelled exactly as the
 * server spells it. That is not a style choice: the filters object is handed straight
 * to `api.get` as its query, so a name that does not match is not a type error; it is
 * a parameter FastAPI silently ignores, and the screen quietly shows an unfiltered
 * list. The status one is the trap: its wire name is `status`, not `candidate_status`.
 */

import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, uploadWithProgress } from '@/api/client';
import type {
  CandidateBulkDelete,
  CandidateBulkDeleteResult,
  ApplyingFor,
  CandidateCreate,
  CandidateCreated,
  CandidatePage,
  ImportPreview,
  ImportResult,
  CandidateRead,
  CandidateUpdate,
  CandidateSource,
  CandidateStatus,
  DocumentationState,
  FlystStage,
  PaymentStatus,
  ReferenceRead,
  RsrState,
} from '@/api/schema';
import type { SortDirection } from '@/components/ui/Table';
import { apiUrl } from '@/lib/env';

import type { CandidateSortKey } from './columns';

export const CANDIDATES_KEY = ['candidates'] as const;
export const REFERENCE_KEY = ['reference'] as const;

/**
 * The whole state of the list screen.
 *
 * Every optional value is typed `X | ''` rather than `X | null`, because the request
 * builder drops empty strings and keeps nulls. `''` is therefore the only way to say
 * "no filter" that survives the trip.
 *
 * `contacted` is a string union for the same reason: `false` is a real filter value,
 * so a boolean leaves nothing to mean "unset". FastAPI parses "true" and "false".
 */
export type CandidateFilters = {
  page: number;
  per_page: number;
  search: string;
  status: CandidateStatus | '';
  source: CandidateSource | '';
  applying_for: ApplyingFor | '';
  department_id: string;
  preferred_position_id: string;
  preferred_country_id: string;
  flyst_stage: FlystStage | '';
  rsr_state: RsrState | '';
  documentation: DocumentationState | '';
  payment_status: PaymentStatus | '';
  contacted: '' | 'true' | 'false';
  received_from: string;
  received_to: string;
  points_min: string;
  points_max: string;
  sort_by: CandidateSortKey;
  sort_dir: SortDirection;
};

export function useCandidates(filters: CandidateFilters, enabled = true) {
  return useQuery({
    queryKey: [...CANDIDATES_KEY, 'list', filters],
    enabled,
    queryFn: () => api.get<CandidatePage>('/api/v1/candidates', { query: { ...filters } }),
    // Keeps the current page on screen while the next one loads, so paging and
    // re-sorting are a redraw rather than a flash of skeleton.
    placeholderData: (previous) => previous,
  });
}

/**
 * The three reference lists.
 *
 * Plain arrays, not paginated envelopes. Cached hard because they change about never
 * and three dropdowns ask for them on every visit.
 */
function useReferenceList(path: string, key: string) {
  return useQuery({
    queryKey: [...REFERENCE_KEY, key],
    queryFn: () => api.get<ReferenceRead[]>(path),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCandidateDepartments() {
  return useReferenceList('/api/v1/candidate-departments', 'candidate-departments');
}

export function usePositions() {
  return useReferenceList('/api/v1/positions', 'positions');
}

export function useCountries() {
  return useReferenceList('/api/v1/countries', 'countries');
}

export function useCandidate(id: string) {
  return useQuery({
    queryKey: [...CANDIDATES_KEY, 'detail', id],
    queryFn: () => api.get<CandidateRead>(`/api/v1/candidates/${id}`),
    // A mistyped id answers 404 and a malformed one 422; neither improves on a retry,
    // and the client already refuses to retry a 4xx.
    enabled: id.length > 0,
  });
}

export function useDeleteCandidates() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CandidateBulkDelete): Promise<CandidateBulkDeleteResult> => {
      if (payload.candidate_ids.length === 1) {
        await api.delete(`/api/v1/candidates/${payload.candidate_ids[0]}`);
        return { affected: 1 };
      }
      return api.post<CandidateBulkDeleteResult>('/api/v1/candidates/bulk/delete', payload);
    },
    retry: false,
    onSettled: async () => {
      await Promise.all(
        [
          CANDIDATES_KEY,
          ['screenings'],
          ['ats'],
          ['meetings'],
          ['dashboard'],
          ['notifications'],
          ['careers'],
        ].map((queryKey) => client.invalidateQueries({ queryKey })),
      );
    },
  });
}

export function useUpdateCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...changes }: CandidateUpdate & { id: string }) =>
      api.patch<CandidateRead>(`/api/v1/candidates/${id}`, changes),

    onSuccess: async (updated) => {
      // The response is the whole record, so the page redraws from the server's own
      // answer rather than waiting for a refetch. Not optimistic: an edit moves a
      // dozen fields at once and the server normalises several of them, so guessing
      // the result would flash a record that never existed.
      queryClient.setQueryData([...CANDIDATES_KEY, 'detail', updated.id], updated);
      // The register behind the page is now stale too.
      await Promise.all(
        [
          CANDIDATES_KEY,
          ['screenings'],
          ['ats'],
          ['meetings'],
          ['dashboard'],
          ['notifications'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });
}

/**
 * Create a candidate.
 *
 * Files are not part of this. The endpoint is JSON and has no file field, and the
 * upload route looks the candidate up first, so the record has to exist before
 * anything can be attached to it. The drawer owns that sequencing.
 *
 * The response carries `possible_duplicates`. They are reported, not refused: the
 * record is created either way, and treating a non-empty list as a failure would be
 * wrong about what just happened.
 */
export function useCreateCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CandidateCreate) =>
      api.post<CandidateCreated>('/api/v1/candidates', body),

    onSuccess: async ({ candidate }) => {
      queryClient.setQueryData([...CANDIDATES_KEY, 'detail', candidate.id], candidate);
      // Every list query, whatever its filters. This is the whole of "the new
      // candidate appears without a manual refresh".
      await Promise.all(
        [
          CANDIDATES_KEY,
          ['screenings'],
          ['ats'],
          ['meetings'],
          ['dashboard'],
          ['notifications'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });
}

export type AttachmentKind = 'cv' | 'passport';

/**
 * Attach one document to a candidate that already exists.
 *
 * A plain function rather than a mutation, because the drawer runs two of these with
 * separate state and a shared `isPending` would light both slots up for one upload.
 *
 * `kind` is a QUERY parameter, not a form field. Put it in the body and the server
 * reads nothing, ignores the field, and answers 422 about a missing query parameter.
 */
export function useAttachCandidateFile() {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      id: string,
      kind: AttachmentKind,
      file: File,
      onProgress?: (fraction: number) => void,
    ) => {
      const body = new FormData();
      body.append('file', file);

      const candidate = await uploadWithProgress<CandidateRead>(
        `/api/v1/candidates/${id}/files`,
        body,
        { query: { kind }, ...(onProgress ? { onProgress } : {}) },
      );

      queryClient.setQueryData([...CANDIDATES_KEY, 'detail', candidate.id], candidate);
      // The register has a CV column, so it is stale too.
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
      return candidate;
    },
    [queryClient],
  );
}

/**
 * Where a candidate's CV can be downloaded.
 *
 * Absolute, because it goes into an `href` and the API is a separate origin: a
 * root-relative path would ask the frontend's own server for it. The session cookie is
 * SameSite=Lax, so it rides along on a top-level navigation.
 */
export function cvUrlFor(candidateId: string, fileId: string): string {
  return apiUrl(`/api/v1/candidates/${candidateId}/files/${fileId}`);
}

/** The same address for any of a candidate's attachments. */
export const fileUrlFor = cvUrlFor;

// --- CSV import ------------------------------------------------------------ //

/**
 * Where the import template is downloaded from.
 *
 * A plain anchor, for the same reason a CV is: `api.get` parses every response as JSON
 * and could not read a `text/csv` body, and `uploadWithProgress` only ever issues a
 * POST. A top-level GET carries the SameSite=Lax session cookie, so the browser saves
 * the file with no code involved at all.
 */
export function importTemplateUrl(): string {
  return apiUrl('/api/v1/candidates/import/template');
}

/**
 * Ask what a file would do. Nothing is written.
 *
 * Sent through `uploadWithProgress` rather than `api.upload` because a spreadsheet on
 * a slow connection is exactly the case where a progress bar is the difference between
 * waiting and giving up.
 */
export function useImportPreview() {
  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress: (fraction: number) => void;
    }) => {
      const body = new FormData();
      body.append('file', file);
      return uploadWithProgress<ImportPreview>('/api/v1/candidates/import/preview', body, {
        onProgress,
      });
    },
  });
}

/** Import the valid rows. The same file is sent again (the preview stored nothing). */
export function useImportCommit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress: (fraction: number) => void;
    }) => {
      const body = new FormData();
      body.append('file', file);
      return uploadWithProgress<ImportResult>('/api/v1/candidates/import/commit', body, {
        onProgress,
      });
    },
    onSuccess: async () => {
      // Everything: the list, the counts, and any detail page already open.
      await Promise.all(
        [
          CANDIDATES_KEY,
          ['screenings'],
          ['ats'],
          ['meetings'],
          ['dashboard'],
          ['notifications'],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });
}
